<script lang="ts">
  import { couponCode } from '$lib/stores/booking-info'
  import { showLoading } from '$lib/stores/layout'
  import { t } from '$lib/translations'
  import type { AvailableService, HomeSelectedInfo } from '$lib/types'
  import { Button } from '$lib/components/ui/button'
  import { calculateIslandFromCoordinates } from '$lib/boundaries'
  import { type AuthModel } from 'pocketbase'

  export let loadingCoupons: boolean
  export let availableServices: AvailableService[] | undefined = undefined
  export let selectedInfo: HomeSelectedInfo
  export let user: AuthModel | undefined = undefined

  let coupon = ''
  let isAnyCouponValid: boolean
  let couponSuccessMessage: string | undefined = undefined
  let couponErrorMessage: string | undefined = undefined

  async function verifyCoupons() {
    isAnyCouponValid = false
    $couponCode = null

    if (!coupon || coupon.length < 1 || !availableServices) {
      couponErrorMessage = $t('common.invalid_coupon')
      couponSuccessMessage = undefined
      isAnyCouponValid = false
      return
    }

    try {
      const results = await Promise.allSettled(
        availableServices.map(async (service) => {
          const response = await fetch('/api/check-coupon', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              coupon: coupon,
              in_out: selectedInfo?.booking.InOut,
              service_type: service.serviceType,
              island: calculateIslandFromCoordinates([
                Number(service.originCoordinates[0]),
                Number(service.originCoordinates[1])
              ]).toString(),
              user: user ? user.id : '',
              one_way_date: service.departureDateTime,
              return_date: service.arrivalDateTime || null
            })
          })

          const jsonData = await response.json()

          if (jsonData.isValid) {
            service.discountType = jsonData.type
            service.discountAmount = jsonData.amount
            return true
          } else {
            service.discountType = undefined
            service.discountAmount = undefined
            return false
          }
        })
      )

      isAnyCouponValid = results.some((result) => result.status === 'fulfilled' && result.value)
      if (isAnyCouponValid) {
        couponErrorMessage = undefined
        couponSuccessMessage = $t('common.valid_coupon')
        couponCode.set(coupon)
      } else {
        couponErrorMessage = $t('common.invalid_coupon')
        couponSuccessMessage = undefined
      }
    } catch (error) {
      isAnyCouponValid = false
      couponErrorMessage = $t('common.invalid_coupon')
      couponSuccessMessage = undefined
    }
  }

  async function clickOnVerifyCoupon() {
    loadingCoupons = true
    $showLoading = true
    await verifyCoupons()
    loadingCoupons = false
    $showLoading = false
  }

  let debounceTimer: number
  function handleKeyDownOnCoupon(event: { key: string }) {
    if (event.key === 'Enter') {
      clearTimeout(debounceTimer)
      debounceTimer = window.setTimeout(() => {
        clickOnVerifyCoupon()
      }, 100)
    }
  }
</script>

<div class="w-[80%] items-center justify-center shadow-lg p-5 bg-cs-light-yellow flex flex-col">
  <div class="w-full items-center justify-center flex flex-col lg:flex-row gap-4">
    <label
      for="coupon"
      class="block text-sm font-medium leading-6 text-gray-900 whitespace-nowrap w-full lg:w-auto"
      style="overflow: visible;"><h2 class="text-gray-900">{$t('common.coupon')}</h2></label
    >
    <div class="flex flex-col w-full lg:w-auto">
      <input
        id="coupon"
        name="coupon"
        bind:value={coupon}
        on:keydown={handleKeyDownOnCoupon}
        type="text"
        class="uppercase px-4 block w-full outline-cs-orange border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-cs-orange sm:text-sm sm:leading-6"
      />
    </div>
    <span>
      <Button
        on:click={clickOnVerifyCoupon}
        disabled={!coupon}
        class="rounded-none transition-all hover:bg-green-600 h-12 flex bg-green-700 uppercase text-white w-full lg:w-auto"
      >
        <span class="flex-grow px-8 text-xs lg:text-sm">{$t('common.validate')}</span>
      </Button>
    </span>
  </div>
  {#if couponErrorMessage}
    <span class="text-center text-xs font-semibold text-red-600">{couponErrorMessage}</span>
  {:else if couponSuccessMessage}
    <span class="text-center text-xs font-semibold text-green-600">{couponSuccessMessage}</span>
  {/if}
</div>
